import {
  BookOpen,
  Bookmark,
  Brain,
  CalendarDays,
  Database,
  Eye,
  FileText,
  FolderKanban,
  GraduationCap,
  Gavel,
  MessageSquare,
  Mic,
  Newspaper,
  Search,
  Settings,
  Users,
} from 'lucide-react';

export const FIXED_AGENT_PATHS = ['/today', '/board', '/matters/dashboard', '/authorities', '/drafting'];

export const SIDEBAR_NAV_ITEMS = [
  { path: '/today', icon: Newspaper, label: 'Today', section: 'Practice' },
  { path: '/board', icon: Gavel, label: 'Court Watch', section: 'Practice' },
  { path: '/matters/dashboard', icon: FolderKanban, label: 'Matter Control', section: 'Practice' },
  { path: '/authorities', icon: Search, label: 'Authority Desk', section: 'Intelligence' },
  { path: '/drafting', icon: FileText, label: 'Drafting Desk', section: 'Intelligence' },
  { path: '/hearings', icon: CalendarDays, label: 'Sabha', section: 'Intelligence' },
  { path: '/jolly', icon: Mic, label: 'Jolly', section: 'Intelligence' },
  { path: '/aina', icon: Eye, label: 'Aina', section: 'Intelligence' },
  { path: '/team-ops', icon: Users, label: 'Team Movement', section: 'Intelligence' },
  { path: '/client-loop', icon: MessageSquare, label: 'Client Updates', section: 'Intelligence' },
  { path: '/templates', icon: BookOpen, label: 'Precedents', section: 'Library' },
  { path: '/knowledge-base', icon: Database, label: 'Knowledge Base', section: 'Library' },
  { path: '/saved', icon: Bookmark, label: 'Saved', section: 'Library' },
  { path: '/student-mode', icon: GraduationCap, label: 'Student Mode', section: 'Library' },
  { path: '/knowledge', icon: Brain, label: 'Practice Memory', section: 'Workspace' },
  { path: '/settings', icon: Settings, label: 'Settings', section: 'Workspace' },
];

export const SIDEBAR_FIXED_ITEMS = FIXED_AGENT_PATHS
  .map((path) => SIDEBAR_NAV_ITEMS.find((item) => item.path === path))
  .filter(Boolean);

export const SIDEBAR_MOVABLE_ITEMS = SIDEBAR_NAV_ITEMS.filter(
  (item) => !FIXED_AGENT_PATHS.includes(item.path),
);

export const DEFAULT_SIDEBAR_NAV_ORDER = SIDEBAR_MOVABLE_ITEMS.map((item) => item.path);

export const MATTER_HOME_PATH = '/matters';
export const DRAFTS_HOME_PATH = '/drafts';
export const SEARCH_HOME_PATH = '/search';
export const TEAM_HOME_PATH = '/team';
export const SETTINGS_HOME_PATH = '/settings';
export const AUTHENTICATED_HOME_PATH = MATTER_HOME_PATH;

export const PRIMARY_SHELL_NAV_ITEMS = [
  { path: MATTER_HOME_PATH, icon: FolderKanban, label: 'Matters' },
  { path: DRAFTS_HOME_PATH, icon: FileText, label: 'Drafts' },
  { path: SEARCH_HOME_PATH, icon: Search, label: 'Search' },
  { path: TEAM_HOME_PATH, icon: Users, label: 'Team', requiresTeam: true },
  { path: SETTINGS_HOME_PATH, icon: Settings, label: 'Settings' },
];

const SHELL_ROUTE_LABELS = [
  { matches: (pathname) => matchesRoutePrefix(pathname, ['/voice-notes', '/jolly']), label: 'Jolly' },
  { matches: (pathname) => matchesRoutePrefix(pathname, ['/red-team', '/aina']), label: 'Aina' },
  { matches: (pathname) => matchesRoutePrefix(pathname, ['/hearings', '/case']), label: 'Sabha' },
  { matches: (pathname) => matchesRoutePrefix(pathname, ['/chat']), label: 'Counsel Chat' },
  { matches: (pathname) => matchesRoutePrefix(pathname, ['/drafts', '/documents', '/drafting']), label: 'Drafts' },
  { matches: (pathname) => matchesRoutePrefix(pathname, ['/war-room', '/matters/dashboard']), label: 'Matters' },
  { matches: (pathname) => matchesRoutePrefix(pathname, ['/search', '/research', '/authorities']), label: 'Search' },
  { matches: (pathname) => matchesRoutePrefix(pathname, ['/team', '/team-ops', '/client-loop']), label: 'Team' },
  { matches: (pathname) => matchesRoutePrefix(pathname, ['/settings']), label: 'Settings' },
  { matches: (pathname) => matchesRoutePrefix(pathname, ['/knowledge']), label: 'Practice Memory' },
  { matches: (pathname) => matchesRoutePrefix(pathname, ['/saved']), label: 'Saved' },
  { matches: (pathname) => matchesRoutePrefix(pathname, ['/today', '/brief']), label: 'Matters' },
  { matches: (pathname) => matchesRoutePrefix(pathname, ['/board']), label: 'Board' },
];

function matchesRoutePrefix(pathname = '', prefixes = []) {
  const normalizedPath = String(pathname || '').split(/[?#]/)[0];
  return prefixes.some((prefix) => (
    normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)
  ));
}

export function normalizeSidebarNavOrder(order) {
  const seen = new Set();
  const allowedPaths = new Set(DEFAULT_SIDEBAR_NAV_ORDER);
  const normalized = [];

  if (Array.isArray(order)) {
    order.forEach((path) => {
      if (!allowedPaths.has(path) || seen.has(path)) return;
      seen.add(path);
      normalized.push(path);
    });
  }

  DEFAULT_SIDEBAR_NAV_ORDER.forEach((path) => {
    if (seen.has(path)) return;
    seen.add(path);
    normalized.push(path);
  });

  return normalized;
}

export function getOrderedSidebarNavItems(order) {
  const normalizedOrder = normalizeSidebarNavOrder(order);
  const movableLookup = new Map(SIDEBAR_MOVABLE_ITEMS.map((item) => [item.path, item]));

  return [
    ...SIDEBAR_FIXED_ITEMS,
    ...normalizedOrder.map((path) => movableLookup.get(path)).filter(Boolean),
  ];
}

export function getPrimarySidebarNavItems({ hasTeamWorkspace = false } = {}) {
  return PRIMARY_SHELL_NAV_ITEMS.filter((item) => !item.requiresTeam || hasTeamWorkspace);
}

export function resolveShellLabel(pathname = '') {
  const normalizedPath = String(pathname || '').split(/[?#]/)[0];
  const primaryMatch = PRIMARY_SHELL_NAV_ITEMS.find((item) => (
    normalizedPath === item.path || normalizedPath.startsWith(`${item.path}/`)
  ));
  if (primaryMatch) return primaryMatch.label;

  const routeMatch = SHELL_ROUTE_LABELS.find((item) => item.matches(normalizedPath));
  if (routeMatch) return routeMatch.label;

  return 'Advoq Workspace';
}

export function isPrimaryNavItemActive(path, pathname = '') {
  if (path === MATTER_HOME_PATH) {
    return matchesRoutePrefix(pathname, [
      '/matters',
      '/matters/dashboard',
      '/war-room',
      '/case',
      '/hearings',
      '/red-team',
      '/aina',
    ]);
  }

  if (path === DRAFTS_HOME_PATH) {
    return matchesRoutePrefix(pathname, ['/drafts', '/documents', '/drafting']);
  }

  if (path === SEARCH_HOME_PATH) {
    return matchesRoutePrefix(pathname, ['/search', '/research', '/authorities']);
  }

  if (path === TEAM_HOME_PATH) {
    return matchesRoutePrefix(pathname, ['/team', '/team-ops', '/client-loop']);
  }

  if (path === SETTINGS_HOME_PATH) {
    return matchesRoutePrefix(pathname, ['/settings']);
  }

  const normalizedPath = String(pathname || '');
  return normalizedPath === path || normalizedPath.startsWith(`${path}/`);
}
